/** Planner tree helpers + branch operations */

export const ITEM_TYPES = [
    { value: "idea", label: "Idea", color: "info" },
    { value: "requirement", label: "Requirement", color: "primary" },
    { value: "deliverable", label: "Deliverable", color: "success" },
    { value: "risk", label: "Risk", color: "warning" },
    { value: "question", label: "Question", color: "secondary" },
    { value: "assumption", label: "Assumption", color: "default" },
];

export const ITEM_STATUSES = [
    { value: "open", label: "Open" },
    { value: "in_progress", label: "In progress" },
    { value: "done", label: "Done" },
    { value: "deferred", label: "Deferred" },
];

export const ITEM_PRIORITIES = [
    { value: "low", label: "Low" },
    { value: "medium", label: "Medium" },
    { value: "high", label: "High" },
    { value: "critical", label: "Critical" },
];

export function newPlannerItemId() {
    return `pi_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function newPlannerGroupId() {
    return `pg_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
}

export function emptyPlannerItem(overrides = {}) {
    return {
        id: newPlannerItemId(),
        title: "",
        body: "",
        item_type: "idea",
        status: "open",
        priority: "medium",
        sort_order: 0,
        ...overrides,
    };
}

export function normalizePlannerGroups(raw) {
    if (!Array.isArray(raw)) return [];
    return raw.map((g, gi) => ({
        id: g.id || newPlannerGroupId(),
        parent_id: g.parent_id || null,
        definition_id: g.definition_id || null,
        group_key: g.group_key || `group_${gi}`,
        label: g.label || "Group",
        scope_notes: g.scope_notes || "",
        sort_order: g.sort_order ?? gi,
        enabled: g.enabled !== false && g.enabled !== 0,
        material_cost: Number(g.material_cost) || 0,
        labor_hours: Number(g.labor_hours) > 0 ? Number(g.labor_hours) : 0,
        items: Array.isArray(g.items)
            ? g.items.map((it, ii) => ({
                  id: it.id || newPlannerItemId(),
                  title: it.title || "",
                  body: it.body || "",
                  item_type: it.item_type || "idea",
                  status: it.status || "open",
                  priority: it.priority || "medium",
                  sort_order: it.sort_order ?? ii,
              }))
            : [],
    }));
}

export function buildPlannerTree(groups) {
    const list = normalizePlannerGroups(groups);
    const byId = Object.fromEntries(list.map((g) => [g.id, { ...g, children: [] }]));
    const roots = [];
    list.forEach((g) => {
        const node = byId[g.id];
        if (g.parent_id && byId[g.parent_id]) {
            byId[g.parent_id].children.push(node);
        } else {
            roots.push(node);
        }
    });
    const sortNodes = (nodes) => {
        nodes.sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
        nodes.forEach((n) => sortNodes(n.children));
    };
    sortNodes(roots);
    return roots;
}

export function groupFromDefinition(def, parentId = null) {
    return {
        id: newPlannerGroupId(),
        parent_id: parentId,
        definition_id: def.id,
        group_key: def.group_key,
        label: def.label,
        scope_notes: def.description || "",
        sort_order: def.sort_order ?? 0,
        enabled: true,
        material_cost: 0,
        labor_hours: 0,
        items: [],
    };
}

export function parseBundleChildren(def) {
    if (!def?.bundle_children) return [];
    return String(def.bundle_children)
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
}

export function branchesToAdd(definition, allDefinitions, parentId = null, existingGroups = []) {
    const existingKeys = new Set((existingGroups || []).map((g) => g.group_key));
    const created = [];

    const makeOne = (def, pid) => {
        if (existingKeys.has(def.group_key)) return null;
        existingKeys.add(def.group_key);
        const node = groupFromDefinition(def, pid);
        created.push(node);
        return node;
    };

    const root = makeOne(definition, parentId);
    if (!root) return [];

    if (definition.is_bundle && parseBundleChildren(definition).length) {
        parseBundleChildren(definition).forEach((key) => {
            const childDef = allDefinitions.find((d) => d.group_key === key);
            if (childDef) makeOne(childDef, root.id);
        });
    }

    return created;
}

export function availableBranchesForParent(parentGroup, allDefinitions, existingGroups) {
    const existingKeys = new Set((existingGroups || []).map((g) => g.group_key));
    const parentKey = parentGroup?.group_key || null;

    return (allDefinitions || [])
        .filter((d) => d.is_active !== 0)
        .filter((d) => !existingKeys.has(d.group_key))
        .filter((d) => {
            if (!parentKey) return !d.parent_group_key;
            return d.parent_group_key === parentKey;
        })
        .sort((a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0));
}

export function itemTypeMeta(type) {
    return ITEM_TYPES.find((t) => t.value === type) || ITEM_TYPES[0];
}

export function countOpenItems(groups) {
    return (groups || []).reduce((acc, g) => {
        if (!g.enabled) return acc;
        return acc + (g.items || []).filter((it) => it.status !== "done" && it.status !== "deferred").length;
    }, 0);
}

export function buildProjectTree(projects) {
    const list = projects || [];
    const byId = Object.fromEntries(list.map((p) => [p.id, { ...p, children: [] }]));
    const roots = [];
    list.forEach((p) => {
        const node = byId[p.id];
        if (p.parent_id && byId[p.parent_id]) {
            byId[p.parent_id].children.push(node);
        } else {
            roots.push(node);
        }
    });
    return roots;
}
